import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FiEdit } from "react-icons/fi";
import { Store, Upload, CreditCard } from "lucide-react";

import DeleteAccountCard from "@/components/profile/DeleteAccountCard";


const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const emptyProfile = {
    username: "",
    display_name: "",
    description: "",
    business_name: "",
    contact_person: "",
    email: "",
    phone: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    tax_id: "",
    bank_details: {
      account_name: "",
      account_number: "",
      sort_code: "",
      bank_name: "",
      iban: "",
      swift: ""
    },
    review_links: {
      google: "",
      facebook: "",
      checkatrade: ""
    }
  };
  const [profileData, setProfileData] = useState(emptyProfile);
  const [baselineProfile, setBaselineProfile] = useState(emptyProfile);
  const hasProfileChanges = JSON.stringify(profileData) !== JSON.stringify(baselineProfile);
  
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [isEditingCompanyLogo, setIsEditingCompanyLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);
  
  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (!user) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error('Error fetching profile:', error);
        toast.error(`Error fetching profile: ${error.message}`);
        return;
      }
      
      // Set avatar URL
      if (data.avatar_url) {
        setAvatarUrl(data.avatar_url);
      }
      
      // Set company logo URL
      if (data.company_logo_url) {
        setCompanyLogoUrl(data.company_logo_url);
      }
      
      // Prepare review links (ensure it's an object with the correct structure)
      let reviewLinks = {
        google: "",
        facebook: "",
        checkatrade: ""
      };
      
      if (data.review_links) {
        // Parse JSON string if needed
        const parsedLinks = typeof data.review_links === 'string'
          ? JSON.parse(data.review_links)
          : data.review_links;
          
        // Merge with default structure to ensure type safety
        reviewLinks = {
          ...reviewLinks,
          ...(parsedLinks || {})
        };
      }
      
      // Parse bank details
      let bankDetails = {
        account_name: "",
        account_number: "",
        sort_code: "",
        bank_name: "",
        iban: "",
        swift: ""
      };
      
      if (data.bank_details) {
        // Parse JSON string if needed
        const parsedBankDetails = typeof data.bank_details === 'string'
          ? JSON.parse(data.bank_details)
          : data.bank_details;
          
        // Merge with default structure
        bankDetails = {
          ...bankDetails,
          ...(parsedBankDetails || {})
        };
      }
      
      // Update state with profile data
      const nextProfile = {
        username: data.username || "",
        display_name: data.display_name || "",
        description: data.description || "",
        business_name: data.business_name || "",
        contact_person: data.contact_person || "",
        email: data.email || user?.email || "",
        phone: data.phone || "",
        website: data.website || "",
        address: data.address || "",
        city: data.city || "",
        postcode: data.postcode || "",
        tax_id: data.tax_id || "",
        bank_details: bankDetails,
        review_links: reviewLinks
      };
      setProfileData(nextProfile);
      setBaselineProfile(nextProfile);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const updateProfile = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        toast.error('You must be logged in to update your profile');
        return;
      }
      
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: profileData.display_name,
          description: profileData.description,
          business_name: profileData.business_name,
          contact_person: profileData.contact_person,
          email: profileData.email,
          phone: profileData.phone,
          website: profileData.website,
          address: profileData.address,
          city: profileData.city,
          postcode: profileData.postcode,
          bank_details: profileData.bank_details,
          review_links: profileData.review_links
        })
        .eq('id', user.id);
        
      if (error) {
        throw error;
      }
      
      toast.success('Profile updated successfully');
      setBaselineProfile(profileData);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file upload for avatar
  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }
      
      if (!user) {
        toast.error('You must be logged in to upload an avatar');
        return;
      }
      
      setIsUploading(true);
      setUploadProgress(0);
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        setIsUploading(false);
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('File type must be JPEG, PNG, GIF or WEBP');
        setIsUploading(false);
        return;
      }
      
      // Upload to storage with progress tracking
      const uploadTask = supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
        
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 300);
      
      const { error: uploadError, data } = await uploadTask;
      
      clearInterval(progressInterval);
      
      if (uploadError) {
        throw uploadError;
      }
      
      setUploadProgress(100);
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
        
      const avatarUrl = urlData.publicUrl;
      
      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', user.id);
        
      if (error) {
        throw error;
      }
      
      setAvatarUrl(avatarUrl);
      toast.success('Avatar updated successfully');
      
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(`Failed to upload avatar: ${error.message}`);
    } finally {
      setIsUploading(false);
      setIsEditingAvatar(false);
    }
  };
  
  // Handle file upload for company logo
  const uploadCompanyLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }
      
      if (!user) {
        toast.error('You must be logged in to upload a company logo');
        return;
      }
      
      setIsUploading(true);
      setUploadProgress(0);
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `company_logo_${user.id}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        setIsUploading(false);
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast.error('File type must be JPEG, PNG, GIF, WEBP or SVG');
        setIsUploading(false);
        return;
      }
      
      // Upload to storage with progress tracking
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 300);
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
        
      clearInterval(progressInterval);
      
      if (uploadError) {
        throw uploadError;
      }
      
      setUploadProgress(100);
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
        
      const companyLogoUrl = urlData.publicUrl;
      
      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ company_logo_url: companyLogoUrl })
        .eq('id', user.id);
        
      if (error) {
        throw error;
      }
      
      setCompanyLogoUrl(companyLogoUrl);
      toast.success('Company logo updated successfully');
      
    } catch (error: any) {
      console.error('Error uploading company logo:', error);
      toast.error(`Failed to upload company logo: ${error.message}`);
    } finally {
      setIsUploading(false);
      setIsEditingCompanyLogo(false);
    }
  };
  
  // Update review links
  const updateReviewLink = (platform: keyof typeof profileData.review_links, value: string) => {
    setProfileData(prev => ({
      ...prev,
      review_links: {
        ...prev.review_links,
        [platform]: value
      }
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Account Profile</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account settings and business details
        </p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="flex-wrap h-auto w-full">
          <TabsTrigger value="profile" className="flex-1 min-w-0">Personal Details</TabsTrigger>
          <TabsTrigger value="business" className="flex-1 min-w-0">Business Details</TabsTrigger>
        </TabsList>

        
        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
              <CardDescription>
                This is how others will see you on the site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={avatarUrl || ""} />
                    <AvatarFallback className="text-lg">
                      {profileData.display_name?.charAt(0) || user?.email?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow"
                    onClick={() => setIsEditingAvatar(true)}
                  >
                    <FiEdit className="h-4 w-4" />
                  </Button>
                  
                  {isEditingAvatar && (
                    <div className="mt-4">
                      <Input 
                        type="file" 
                        accept="image/*"
                        onChange={uploadAvatar}
                        disabled={isUploading}
                        className="max-w-xs"
                      />
                      {isUploading && (
                        <div className="mt-2">
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Uploading: {uploadProgress}%</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div>
                  <h3 className="font-medium">{profileData.display_name || user?.email}</h3>
                  <p className="text-sm text-muted-foreground">@{profileData.username}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  value={profileData.display_name}
                  onChange={(e) => setProfileData({...profileData, display_name: e.target.value})}
                  placeholder="Your display name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Bio</Label>
                <Textarea
                  id="description"
                  value={profileData.description}
                  onChange={(e) => setProfileData({...profileData, description: e.target.value})}
                  placeholder="Tell us a bit about yourself"
                  rows={4}
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={updateProfile} disabled={loading || !hasProfileChanges}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardFooter>
          </Card>


          <div className="mt-8">
            <DeleteAccountCard />
          </div>
        </TabsContent>
        
        <TabsContent value="business" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
              <CardDescription>
                Update your business information for invoices and receipts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Identity Section */}
              <div className="rounded-lg border p-4 pb-5">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">Company Identity</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name</Label>
                    <Input
                      id="business_name"
                      value={profileData.business_name}
                      onChange={(e) => setProfileData({...profileData, business_name: e.target.value})}
                      placeholder="Your business name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_logo">Company Logo</Label>
                    <div className="flex items-center space-x-4">
                      <div className="relative h-24 w-24 overflow-hidden rounded-md border bg-muted flex items-center justify-center">
                        {companyLogoUrl ? (
                          <img 
                            src={companyLogoUrl} 
                            alt="Company logo" 
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <Upload className="h-8 w-8 text-muted-foreground" />
                        )}
                        
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="absolute bottom-1 right-1 h-6 w-6 rounded-full shadow"
                          onClick={() => setIsEditingCompanyLogo(true)}
                        >
                          <FiEdit className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {isEditingCompanyLogo && (
                        <div className="flex-1">
                          <Input 
                            id="company_logo"
                            type="file" 
                            accept="image/*"
                            onChange={uploadCompanyLogo}
                            disabled={isUploading}
                          />
                          {isUploading && (
                            <div className="mt-2">
                              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full" 
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Uploading: {uploadProgress}%</p>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Upload your company logo (PNG, JPG, SVG)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bank Details Section */}
              <div className="rounded-lg border p-4 pb-5">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">Bank Details</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="account_name">Account Name</Label>
                    <Input
                      id="account_name"
                      value={profileData.bank_details.account_name}
                      onChange={(e) => setProfileData({
                        ...profileData, 
                        bank_details: {
                          ...profileData.bank_details, 
                          account_name: e.target.value
                        }
                      })}
                      placeholder="Account holder name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="account_number">Account Number</Label>
                      <Input
                        id="account_number"
                        value={profileData.bank_details.account_number}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          bank_details: {
                            ...profileData.bank_details, 
                            account_number: e.target.value
                          }
                        })}
                        placeholder="Account number"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="sort_code">Sort Code</Label>
                      <Input
                        id="sort_code"
                        value={profileData.bank_details.sort_code}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          bank_details: {
                            ...profileData.bank_details, 
                            sort_code: e.target.value
                          }
                        })}
                        placeholder="Sort code (e.g. 12-34-56)"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={profileData.bank_details.bank_name}
                      onChange={(e) => setProfileData({
                        ...profileData, 
                        bank_details: {
                          ...profileData.bank_details, 
                          bank_name: e.target.value
                        }
                      })}
                      placeholder="Bank name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="iban">IBAN (International)</Label>
                      <Input
                        id="iban"
                        value={profileData.bank_details.iban}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          bank_details: {
                            ...profileData.bank_details, 
                            iban: e.target.value
                          }
                        })}
                        placeholder="IBAN for international transfers"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="swift">SWIFT/BIC Code</Label>
                      <Input
                        id="swift"
                        value={profileData.bank_details.swift}
                        onChange={(e) => setProfileData({
                          ...profileData, 
                          bank_details: {
                            ...profileData.bank_details, 
                            swift: e.target.value
                          }
                        })}
                        placeholder="SWIFT/BIC code"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact Information Section */}
              <div className="rounded-lg border p-4 pb-5">
                <h3 className="font-semibold mb-4">Contact Information</h3>
              
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      value={profileData.contact_person}
                      onChange={(e) => setProfileData({...profileData, contact_person: e.target.value})}
                      placeholder="Contact person name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      placeholder="Your email address"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      placeholder="Your phone number"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={profileData.website}
                      onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                      placeholder="Your website URL"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      placeholder="e.g. 123 High Street"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">Town</Label>
                    <Input
                      id="city"
                      value={profileData.city}
                      onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                      placeholder="e.g. London"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input
                      id="postcode"
                      value={profileData.postcode}
                      onChange={(e) => setProfileData({...profileData, postcode: e.target.value})}
                      placeholder="e.g. SW1A 1AA"
                    />
                  </div>
                  
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={updateProfile} disabled={loading || !hasProfileChanges}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
